// src/coverCheck/index.js - the manager app's Cover check tab (v132.97).
//
// docs/cover-check-plan.md. Stage 1: the settings the store rota's cover check
// reads - sales per person per country and the hourly sales pattern, loaded
// from the Cubes "Sales by Hour" report. Head Office edits them; everyone else
// sees them read-only (the Worker enforces Head Office on the write).
//
// Auth = the admin Bearer (DB.getToken), absolute Worker URL (DB.workerUrl).

import { COUNTRIES, COVER_DAYS, DEFAULT_TARGET, parseCubesHourly, sharesFromSales, formatHour } from '../worker/cover-check.js';
import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used

const CUR = { UAE: 'AED', Qatar: 'QAR' };
let _config = null;
const _pending = {};   // country -> { pattern, source } uploaded but not saved yet

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function api(path, method, body) {
  const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return fetch(base + path, { cache: 'no-store', method: method || 'GET', headers, body: body ? JSON.stringify(body) : undefined });
}
function root() { return document.getElementById('coverCheckRoot'); }
function isHeadOffice() {
  return typeof currentAdmin !== 'undefined' && currentAdmin && String(currentAdmin.role || '').toLowerCase() === 'admin';
}
// Today on the Dubai calendar (the Worker and every app are UTC; Dubai is UTC+4).
function dubaiToday() { return new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 10); }
function toast(msg) { if (typeof showToast === 'function') showToast(msg); }

// A small heatmap of the pattern: 7 days by the trading hours, darker = more.
export function patternPreview(pattern) {
  if (!pattern) return '<div style="font-size:12px;color:var(--sub)">No pattern loaded yet.</div>';
  const hours = [];
  for (let h = 8; h <= 23; h++) hours.push(h);
  const max = Math.max(0.0001, ...pattern.flat());
  let html = '<div style="overflow-x:auto"><table style="border-collapse:collapse;font-size:11px"><tr><th></th>' +
    hours.map((h) => '<th style="padding:2px 4px;font-weight:600;color:var(--sub)">' + formatHour(h) + '</th>').join('') + '</tr>';
  COVER_DAYS.forEach((d, i) => {
    html += '<tr><th style="padding:2px 6px;text-align:start">' + d + '</th>' + hours.map((h) => {
      const v = pattern[i][h] || 0;
      const a = Math.round((v / max) * 85);
      return '<td title="' + d + ' ' + formatHour(h) + ': ' + (v * 100).toFixed(1) + '% of the day" style="width:30px;height:20px;text-align:center;background:rgba(14,92,58,' + (a / 100) + ');color:' + (a > 45 ? '#fff' : 'inherit') + '">' + (v >= 0.005 ? Math.round(v * 100) : '') + '</td>';
    }).join('') + '</tr>';
  });
  return html + '</table><div style="font-size:11px;color:var(--sub);margin-top:4px">Numbers are each hour\'s share of that day\'s sales, in %.</div></div>';
}

function countryCard(c, ho, typed) {
  const cfg = _config || {};
  const pend = _pending[c];
  const pattern = pend ? pend.pattern : (cfg.pattern && cfg.pattern[c]) || null;
  // A target typed but not saved yet wins over the saved one (an upload redraws the page).
  const target = (typed != null && typed !== '') ? typed : ((cfg.target && cfg.target[c]) || DEFAULT_TARGET[c]);
  const src = pend ? pend.source : (cfg.source && cfg.source[c]) || '';
  const state = pend ? '<span style="color:var(--ui-warn);font-weight:600">Uploaded, not saved yet</span>'
    : (cfg.configured && cfg.configured[c]) ? '<span style="color:var(--ok-deep);font-weight:600">Set up</span>'
    : '<span style="color:var(--ui-warn);font-weight:600">Not set up: stores in ' + c + ' see "Not set up yet"</span>';
  return '<div class="card" style="margin-bottom:14px">' +
    '<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap"><div class="card-title" style="margin-bottom:0">' + c + '</div><div style="font-size:12px">' + state + '</div></div>' +
    '<div style="display:flex;gap:18px;flex-wrap:wrap;align-items:flex-end;margin:10px 0">' +
      '<label style="font-size:12px;display:grid;gap:4px">One person on the floor per ' + CUR[c] + ' of sales an hour' +
        (ho ? '<input type="number" id="ccTarget_' + c + '" min="50" max="5000" step="1" value="' + esc(target) + '" style="width:120px;padding:6px;border:1px solid var(--border);border-radius:6px">'
            : '<b style="font-size:15px">' + esc(target) + '</b>') + '</label>' +
      (ho ? '<label style="font-size:12px;display:grid;gap:4px">Cubes "Sales by Hour" report (.xlsx)<input type="file" id="ccFile_' + c + '" accept=".xlsx,.xls" onchange="CoverCheck.upload(\'' + c + '\', this)"></label>' : '') +
    '</div>' +
    (src ? '<div style="font-size:11px;color:var(--sub);margin-bottom:8px">Pattern from: ' + esc(src) + '</div>' : '') +
    patternPreview(pattern) +
  '</div>';
}

export async function render() {
  const el = root();
  if (!el) return;
  el.innerHTML = '<div style="color:var(--sub);font-size:13px">Loading...</div>';
  try {
    const r = await api('/api/cover-check/config');
    _config = r.ok ? await r.json() : null;
  } catch (e) { _config = null; }
  draw();
}

function draw() {
  const el = root();
  if (!el) return;
  const ho = isHeadOffice();
  const cfg = _config || {};
  // Keep any target typed but not saved yet: upload() calls draw(), which used
  // to rebuild the inputs from the saved config and wipe what was typed.
  const typed = {};
  COUNTRIES.forEach((c) => { const i = document.getElementById('ccTarget_' + c); if (i) typed[c] = i.value; });
  el.innerHTML =
    '<div style="margin-bottom:14px"><div style="font-size:18px;font-weight:800">Cover check</div>' +
    '<div style="font-size:13px;color:var(--sub);max-width:780px">Each store\'s rota shows people rostered against people needed, hour by hour. ' +
    'People needed = the store\'s daily target, spread by the hourly sales pattern below, divided by the sales per person. Never fewer than one.</div></div>' +
    (ho ? '' : '<div class="card" style="margin-bottom:14px;font-size:13px">These settings are set by Head Office.</div>') +
    COUNTRIES.map((c) => countryCard(c, ho, typed[c])).join('') +
    (ho ? '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap"><button class="btn btn-primary" id="ccSave" onclick="CoverCheck.save()">Save settings</button>' +
      '<span style="font-size:12px;color:var(--sub)">' + (cfg.updated_at ? 'Last saved ' + esc(String(cfg.updated_at).slice(0, 16).replace('T', ' ')) + (cfg.updated_by ? ' by ' + esc(cfg.updated_by) : '') : 'Not saved yet') + '</span></div>' : '') +
    '<div id="ccArea"></div>';
  loadArea();
}

// ── Stage 3 (v132.99): this week across my stores ─────────────────────────
// Read-only. Head Office sees every store, an area manager their own area
// (the Worker scopes it). Moves still happen in the store's rota with
// "Add Cover" / "At Another Store"; nothing here writes.
let _week = null;   // Monday 'YYYY-MM-DD' of the week on screen (null = this week)

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
export function dayLabel(dateStr) {
  const d = new Date(dateStr + 'T12:00:00Z');
  return COVER_DAYS[d.getUTCDay()] + ' ' + d.getUTCDate();
}
export function cellFor(day, configured) {
  if (!configured) return { cls: 'none', text: 'not set up', title: 'Head Office has not loaded this country\'s sales-by-hour pattern yet' };
  if (!day.hasRota) return { cls: 'none', text: 'no rota', title: 'No shifts rostered for this day yet' };
  const runs = (day.runs || []).slice(0, 2).map((r) => formatHour(r.from) + ' to ' + formatHour(r.to)).join(', ');
  if (day.short >= 0.5) return { cls: 'short', text: '-' + Math.round(day.short), title: Math.round(day.short) + ' person-hours short' + (runs ? ': ' + runs : '') + '; ' + Math.round(day.spare) + ' spare' };
  if (day.spare >= 9) return { cls: 'spare', text: '+' + Math.round(day.spare), title: Math.round(day.spare) + ' person-hours spare: about a whole shift more than the trade needs' };
  return { cls: 'ok', text: 'ok', title: Math.round(day.spare) + ' spare hours (the normal afternoon overlap)' };
}
const CELL = { short: 'background:var(--arota-cc-short-bg);color:var(--arota-cc-short-ink);font-weight:700', spare: 'background:var(--arota-cc-spare-bg);color:var(--arota-cc-spare-ink);font-weight:700', ok: 'background:var(--arota-cc-ok-bg);color:var(--arota-cc-ok-ink)', none: 'background:var(--arota-cc-none-bg);color:var(--arota-cc-none-ink)' };

export async function loadArea(week) {
  if (week !== undefined) _week = week;
  const el = document.getElementById('ccArea');
  if (!el) return;
  el.innerHTML = '<div style="color:var(--sub);font-size:13px;margin:12px 0">Loading this week...</div>';
  let data = null;
  try {
    const r = await api('/api/admin/cover-check' + (_week ? '?week=' + encodeURIComponent(_week) : ''));
    data = r.ok ? await r.json() : null;
  } catch (e) { data = null; }
  if (!data) { el.innerHTML = '<div class="card" style="margin-top:14px;font-size:13px">This week across the stores could not be loaded. Try again in a moment.</div>'; return; }
  _week = data.weekMonday;
  const byArea = {};
  (data.stores || []).forEach((s) => { (byArea[s.area || 'Other'] = byArea[s.area || 'Other'] || []).push(s); });
  const sugBy = {};
  (data.suggestions || []).forEach((x) => { (sugBy[x.area || 'Other'] = sugBy[x.area || 'Other'] || []).push(x); });
  let html = '<div class="card" style="margin-top:18px">' +
    '<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap">' +
      '<div><div class="card-title" style="margin-bottom:2px">This week across ' + (data.scope === 'all' ? 'every store' : 'my stores') + '</div>' +
      '<div style="font-size:12px;color:var(--sub)">Week of Monday ' + esc(dayLabel(data.weekMonday)) + ' (Qatar from the Sunday before). Person-hours: <b style="color:var(--arota-cc-short-ink)">-6</b> short, <b style="color:var(--arota-cc-spare-ink)">+10</b> about a whole spare shift, ok = right, give or take the normal afternoon overlap.</div></div>' +
      '<div style="display:flex;gap:6px"><button class="btn btn-outline" onclick="CoverCheck.loadArea(\'' + addDays(data.weekMonday, -7) + '\')">&lsaquo; Previous</button>' +
      '<button class="btn btn-outline" onclick="CoverCheck.loadArea(null)">This week</button>' +
      '<button class="btn btn-outline" onclick="CoverCheck.loadArea(\'' + addDays(data.weekMonday, 7) + '\')">Next &rsaquo;</button></div>' +
    '</div>';
  const areaNames = Object.keys(byArea).sort();
  if (!areaNames.length) html += '<div style="font-size:13px;margin-top:10px">No stores in your scope.</div>';
  areaNames.forEach((area) => {
    const list = byArea[area].slice().sort((a, b) => b.short - a.short || a.store.localeCompare(b.store));
    const days = list[0].days;
    html += '<div style="margin-top:16px"><div style="font-weight:700;margin-bottom:6px">' + esc(area) + '</div><div style="overflow-x:auto"><table style="border-collapse:collapse;font-size:12px;min-width:100%"><thead><tr><th style="text-align:start;padding:4px 6px">Store</th>' +
      days.map((d) => '<th style="padding:4px 6px">' + esc(dayLabel(d.date)) + '</th>').join('') + '<th style="padding:4px 6px">Week short / spare</th></tr></thead><tbody>';
    list.forEach((s) => {
      html += '<tr><td style="padding:4px 6px;white-space:nowrap" translate="no">' + esc(s.store) + '</td>' + s.days.map((d) => {
        const c = cellFor(d, s.configured);
        return '<td title="' + esc(c.title) + '" style="text-align:center;padding:4px 6px;border:1px solid var(--card);' + CELL[c.cls] + '">' + esc(c.text) + '</td>';
      }).join('') + '<td style="text-align:center;padding:4px 6px;white-space:nowrap">' + (s.configured ? '<b style="color:var(--arota-cc-short-ink)">' + Math.round(s.short) + '</b> / <span style="color:var(--arota-cc-spare-ink)">' + Math.round(s.spare) + '</span>' : '') + '</td></tr>';
    });
    html += '</tbody></table></div>';
    const sug = sugBy[area] || [];
    if (sug.length) {
      html += '<div style="font-size:12px;margin-top:8px;display:grid;gap:3px"><b>Right place: same-day moves worth a look</b>' + sug.map((x) =>
        '<div>' + esc(dayLabel(x.date)) + ': ' + esc(x.from) + ' has about ' + Math.round(x.spare) + ' spare person-hours; ' + esc(x.to) + ' is ' + Math.round(x.short) + ' short' + (x.when ? ' (' + esc(x.when) + ')' : '') + '.</div>').join('') +
        '<div style="color:var(--sub)">To act on one, use Add Cover in the short store\'s rota in the staff app. Nothing here changes a rota.</div></div>';
    }
    html += '</div>';
  });
  el.innerHTML = html + '</div>';
}

export function upload(country, input) {
  const f = input && input.files && input.files[0];
  if (!f) return;
  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') { toast('Loading the spreadsheet reader...'); ensureXlsx().then(function () { upload(country, input); }, function (e) { toast(e.message); }); return; }
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });
      const parsed = parseCubesHourly(aoa);
      _pending[country] = { pattern: sharesFromSales(parsed.sales), source: f.name + ' (uploaded ' + dubaiToday() + ')' };
      draw();
      toast(country + ' pattern read. Check the preview, then Save settings.');
    } catch (err) {
      toast('Could not read that file: ' + (err && err.message ? err.message : 'unknown error'));
    }
  };
  reader.readAsArrayBuffer(f);
}

export async function save() {
  const body = { target: {}, pattern: {}, source: {} };
  COUNTRIES.forEach((c) => {
    const el = document.getElementById('ccTarget_' + c);
    if (el && el.value !== '') body.target[c] = Number(el.value);
    if (_pending[c]) { body.pattern[c] = _pending[c].pattern; body.source[c] = _pending[c].source; }
  });
  const btn = document.getElementById('ccSave');
  if (btn) btn.disabled = true;
  try {
    const r = await api('/api/admin/cover-check/config', 'POST', body);
    const data = await r.json().catch(() => ({}));
    if (!r.ok) { toast('Not saved: ' + (data.error || data.message || r.status)); return; }
    COUNTRIES.forEach((c) => { delete _pending[c]; });
    toast('Cover check settings saved');
    await render();
  } catch (e) {
    toast('Not saved: ' + (e && e.message ? e.message : 'network error'));
  } finally {
    if (btn) btn.disabled = false;
  }
}
